/**
 * Arabic strings. §5.8: no inline literals in components — every user-facing string
 * lives here and in `en.ts`, **both files, always**. A string added to one locale and
 * not the other is a defect, not a TODO (§23.10).
 */
export const ar = {
  meta: {
    locale: 'ar',
    dir: 'rtl',
    name: 'العربية',
  },

  app: {
    title: 'عون',
    subtitle: 'لوحة التحكم',
    signOut: 'خروج',
    account: 'الحساب',
    language: 'English',
    loading: 'جارٍ التحميل…',
    menuOpen: 'فتح القائمة',
    menuClose: 'إغلاق القائمة',
    menuCollapse: 'تضييق القائمة',
    menuExpand: 'توسيع القائمة',
    liveOn: 'التحديث الحيّ متّصل',
    liveConnecting: 'جارٍ الاتّصال بالبثّ الحيّ…',
    liveOff: 'لا بثّ حيّ — البيانات من الخادم عند كل تحديث',
    appearance: 'المظهر',
    appearanceSystem: 'حسب النظام',
    appearanceLight: 'نهاري',
    appearanceDark: 'ليلي',
  },

  nav: {
    volunteers: 'المتطوّعون',
    missions: 'المهامّ',
    committee: 'لجنة المشاركات',
    users: 'الصلاحيات',
    audit: 'سجلّ التدقيق',
    statistics: 'الإحصاءات',
    exports: 'التصدير',
    points: 'جدول النقاط',
    branding: 'الهوية البصرية',
    themes: 'السِمات',
    committeeSettings: 'إعدادات اللجنة',
    sectionOperations: 'العمليّات',
    sectionAdministration: 'الإدارة',
  },

  themes: {
    paletteGroup: 'اللوحة المعروضة',
    paletteDay: 'نهارًا',
    paletteNight: 'ليلًا',
    noDaylight: 'هذه السِمة بلا لوحة نهاريّة، فالألوان أدناه هي ما يُرسَم في الحالتين.',
    title: 'السِمات',
    subtitle: 'ألوان المنصّة وخطّها وحوافّها (§٧). تُحرَّر من هنا لا من قاعدة البيانات، وتصل كلّ جهاز بلا إصدار جديد.',
    heading: 'السِمات',
    name: 'الاسم',
    version: 'الإصدار',
    state: 'الحالة',
    actions: 'إجراءات',
    live: 'المعروضة الآن',
    draft: 'مسوّدة',
    edit: 'تحرير',
    close: 'إغلاق',
    activate: 'اعرضها للجميع',
    add: 'سِمة جديدة',
    cancel: 'إلغاء',
    save: 'احفظ',
    saveFailed: 'تعذّر الحفظ.',
    unreadable: 'وثيقة هذه السِمة غير قابلة للقراءة، فلا يمكن تحريرها من هنا.',
    editing: 'تحرير',
    editingDraft: 'مسوّدة: الحفظ لا يغيّر ما يراه الميدان. «اعرضها للجميع» هو ما ينشرها.',
    editingLive: '⚠️ هذه السِمة معروضة الآن — كلّ حفظ هنا يصل كلّ جهاز في الميدان مباشرة.',
    typography: 'مقاس الخطّ',
    radius: 'استدارة الحوافّ',
    emptyTitle: 'لا سِمات',
    empty: 'لم تُنشأ سِمة بعد.',
    tokens: {
      background: 'الخلفيّة',
      surface: 'السطح',
      surfaceElevated: 'سطح مرتفع',
      border: 'الحدود',
      primary: 'اللون الأساس',
      primaryForeground: 'النصّ فوق الأساس',
      secondary: 'اللون الثانوي',
      foreground: 'النصّ',
      mutedForeground: 'نصّ خافت',
      success: 'نجاح',
      warning: 'تحذير',
      danger: 'خطر',
      info: 'معلومة',
      missionMissing: 'مهمّة مفقود',
      missionStranded: 'مهمّة عالق',
      trackLine: 'خطّ المسار',
      coverageFill: 'تعبئة المُغطّى',
      gapFill: 'تعبئة الفجوة',
    },
  },

  points: {
    title: 'جدول النقاط',
    subtitle: 'ما تساويه كلّ مشاركة (§23.6). يُحرَّر من هنا لا من قاعدة البيانات.',
    heading: 'البنود',
    item: 'البند',
    code: 'الرمز',
    codeHint: 'حروف إنجليزية صغيرة وأرقام وشرطات',
    nameAr: 'الاسم بالعربيّة',
    nameEn: 'الاسم بالإنجليزيّة',
    value: 'النقاط',
    state: 'الحالة',
    active: 'مفعّل',
    retired: 'متقاعد',
    retire: 'تقاعد',
    restore: 'أعِده',
    add: 'بند جديد',
    cancel: 'إلغاء',
    save: 'احفظ',
    saveFailed: 'تعذّر الحفظ.',
    emptyTitle: 'لا بنود',
    empty: 'لم يُضَف أيّ بند بعد.',
  },

  branding: {
    title: 'الهوية البصرية',
    subtitle: 'شعار الجمعيّة وختم الشهادات (§7). يُرفعان من هنا.',
    slots: {
      logo: 'شعار الجمعيّة',
      seal: 'ختم الشهادات',
      vision: 'شعار رؤية ٢٠٣٠',
      licence: 'علامة الترخيص',
    },
    ledes: {
      logo: 'يظهر في التطبيق واللوحة وأعلى الشهادة.',
      seal: 'يُختم به أسفل شهادة الشكر (§23.6).',
      vision: 'أعلى الشهادة من جهة البداية.',
      licence: 'رخصة الجمعيّة من الوزارة، أسفل الشهادة.',
    },
    none: 'لم تُرفع صورة بعد',
    loading: 'يُحمَّل…',
    keyMissing: 'الثيم يشير إلى ملفّ غير موجود. ارفع صورةً جديدة.',
    replace: 'ارفع صورة',
    uploading: 'يُرفع…',
    uploadFailed: 'تعذّر الرفع.',
  },

  committeeSettings: {
    title: 'إعدادات اللجنة',
    subtitle: 'عدد الأعضاء ومهلة التصويت (§23.2). يُضبطان من هنا لا من ملفّ الإعداد.',
    heading: 'اللجنة',
    size: 'عدد الأعضاء',
    sizeHint: 'أجاب العميل: ٢٧ عضوًا.',
    quorum: 'النصاب',
    quorumNote: 'أغلبية الأعضاء، تُحسب من العدد ولا تُكتب.',
    window: 'مهلة التصويت (بالدقائق)',
    windowHint: 'المهلة المقترحة في نموذج الإحالة؛ يضبط رئيس العمليّات مهلة كلّ مهمّة.',
    save: 'احفظ',
    saving: 'يُحفظ…',
    saved: 'حُفِظ.',
    saveFailed: 'تعذّر الحفظ.',
  },

  audit: {
    title: 'سجلّ التدقيق',
    subtitle: 'من فعل ماذا، ومتى. يُكتب مع كلّ تغيير في النظام (§12.7).',
    filters: 'التصفية',
    entries: 'السجلّات',
    entityType: 'النوع',
    includeSystem: 'إظهار صفوف النظام (رموز التحديث والأجهزة)',
    anyEntity: 'كلّ الأنواع',
    action: 'الإجراء',
    anyAction: 'كلّ الإجراءات',
    actions: { Added: 'إضافة', Modified: 'تعديل', Deleted: 'حذف', Read: 'قراءة' },
    actor: 'المنفِّذ',
    actorPlaceholder: 'معرّف المستخدم',
    clear: 'أزل التصفية',
    when: 'الوقت',
    change: 'التغيير',
    show: 'اعرض',
    hide: 'أخفِ',
    before: 'قبل',
    after: 'بعد',
    nothing: 'لا شيء',
    bySystem: 'النظام',
    emptyTitle: 'لا سجلّات',
    empty: 'لم يُسجَّل شيء بعد.',
    emptyNarrowed: 'لا سجلّات تطابق هذه التصفية.',
  },

  exports: {
    title: 'التصدير',
    subtitle: 'ملفّات إكسل تُبنى في الخلفيّة، ثمّ تُنزَّل من هنا (§19).',
    ask: 'اطلب ملفّاً',
    askHint: 'اترك المدّة فارغة لتشمل كلّ شيء. الملفّ يُبنى في الخلفيّة ويظهر في الجدول أدناه.',
    askAction: 'اطلب التصدير',
    asking: 'يُرسَل…',
    askFailed: 'تعذّر إرسال الطلب. حاول مرّة أخرى.',
    kind: 'المحتوى',
    kinds: {
      Missions: 'المهامّ',
      Volunteers: 'المتطوّعون',
      Participation: 'المشاركات',
    },
    from: 'من',
    to: 'إلى',
    range: 'المدّة',
    everything: 'كلّ شيء',
    openStart: 'من البداية',
    openEnd: 'حتّى الآن',
    requests: 'الطلبات',
    status: 'الحالة',
    statuses: {
      Queued: 'في الانتظار',
      Building: 'قيد التحضير',
      Ready: 'جاهز',
      Failed: 'أخفق',
    },
    rows: 'عدد الصفوف',
    requestedAt: 'وقت الطلب',
    file: 'الملفّ',
    download: 'تنزيل',
    building: 'قيد التحضير…',
    failed: 'أخفق التصدير.',
    emptyTitle: 'لا طلبات',
    empty: 'لم يُطلب تصدير بعد.',
  },

  missions: {
    strandedCreate: 'بلاغ عالق',
    strandedCreateTitle: 'تسجيل بلاغ عالق عن متصل',
    strandedCreateLede: 'للمتصل هاتفياً بلا تطبيق. يُفتح البحث فوراً، ويُبلَّغ متطوّعو المنطقة.',
    strandedNameLabel: 'اسم العالق',
    strandedNameRequired: 'الاسم مطلوب، ولا يقلّ عن أربعة أحرف.',
    strandedPhoneLabel: 'رقم التواصل',
    strandedPhoneRequired: 'رقم الجوال مطلوب.',
    strandedGenderLabel: 'الجنس',
    strandedLatitude: 'خط العرض',
    strandedLongitude: 'خط الطول',
    strandedWhereRequired: 'الموقع مطلوب — اقرأه من جوال المتصل.',
    strandedCompanions: 'عدد المرافقين',
    strandedCompanionsInvalid: 'عدد المرافقين رقم صحيح، صفر فأكثر.',
    strandedNeeds: 'الاحتياج (اختياري)',
    strandedNeedsPlaceholder: 'مثال: ماء، سحب مركبة، إسعاف',
    strandedConfirm: 'افتح البحث',
    strandedOpened: 'فُتح البحث وأُبلغ متطوّعو المنطقة.',
    sending: 'جارٍ الإرسال…',
    genders: {
      Male: 'ذكر',
      Female: 'أنثى',
    },

    subCreate: 'إنشاء مهمّة فرعيّة',
    subCreateTitle: 'فتح التسجيل في مهمّة فرعيّة',
    subCreateLede: 'حجّ أو عمرة أو معرض أو مناسبة أو محاضرة. تُجمع الأسماء قبل الموعد، والمحادثة تُفتح ببدء المهمّة لا بالتسجيل.',
    subCreateConfirm: 'افتح التسجيل',
    subTitleLabel: 'اسم المهمّة',
    subTitlePlaceholder: 'مثال: معرض التوعية بالسلامة',
    subTitleRequired: 'اسم المهمّة مطلوب.',
    subKindLabel: 'النوع',
    subRegionLabel: 'المنطقة',
    subCityLabel: 'المحافظة',
    subWhenLabel: 'الموعد',
    subWhenRequired: 'الموعد مطلوب.',
    subWhenPast: 'الموعد يجب أن يكون في المستقبل.',
    subSeatsLabel: 'عدد المقاعد',
    subSeatsHint: 'اتركه فارغًا لعدد غير محدود',
    subSeatsInvalid: 'عدد المقاعد يجب أن يكون رقمًا أكبر من صفر.',
    subLatitudeLabel: 'خط العرض',
    subLongitudeLabel: 'خط الطول',
    subWhereRequired: 'الموقع مطلوب.',
    subDescriptionLabel: 'وصف (اختياري)',
    subCreated: 'فُتح التسجيل.',
    subWaiting: 'قائمة الانتظار',
    subExclude: 'استبعاد',
    subExcludeTitle: 'استبعاد من المهمّة',
    subExcludeLede: 'يُخلى مقعده لمن ينتظره، ويُبلَّغ بالسبب.',
    subExcludeReason: 'سبب الاستبعاد',
    subExcludeReasonRequired: 'السبب مطلوب.',
    subExcluded: 'استُبعد.',
    subPlaces: 'المقاعد',
    subUnlimited: 'غير محدود',
    subKinds: {
      Hajj: 'حجّ',
      Umrah: 'عمرة',
      Exhibition: 'معرض',
      SocialEvent: 'مناسبة',
      Lecture: 'محاضرة',
      Other: 'أخرى',
    },
    chatLocation: 'موقع مُرسَل',
    chatHeading: 'محادثة المهمّة',
    chatFollowOnly: 'ما يقوله الفريق في الميدان، مباشرةً.',
    chatReadOnly: 'متابعة فقط',
    chatEmpty: 'لم يتحدّث أحد بعد',
    chatEmptyHint: 'تُفتح المحادثة ببدء المهمّة، وتظهر هنا أوّل رسالة فور إرسالها.',
    chatSentBy: 'أرسلها {name}',
    chatVoice: 'رسالة صوتيّة',
    chatImage: 'صورة',
    chatVideo: 'مقطع فيديو',
    chatOpenMedia: 'فتح',
    chatMediaFailed: 'تعذّر فتح الملف. حاول مرّة أخرى.',
    heading: 'لوحة المهامّ',
    lede: 'كل المهامّ، وما يحتاج قراراً منها في الأعلى.',
    empty: 'لا توجد مهامّ مطابقة.',
    title: 'المهمّة',
    type: 'النوع',
    status: 'الحالة',
    region: 'المنطقة',
    area: 'المساحة',
    members: 'الأعضاء',
    elapsed: 'منذ الحادثة',
    created: 'أُنشئت',
    filterAll: 'الكل',
    areaUnit: 'كم²',
    noRegion: '—',
    notStarted: 'لم تبدأ',
    coverageHeading: 'التغطية',
    coverageCovered: 'مُغطّى',
    coverageGap: 'فجوة',
    coverageArea: 'مساحة المهمّة',
    coverageMembers: 'أعضاء',
    coveragePoints: 'نقاط تتبّع',
    coverageRadius: 'نصف قطر الرؤية',
    positionMinutesAgo: 'قبل {n} د',
    positionHoursAgo: 'قبل {n} س',
    coverageUnavailable: 'لا تتوفّر تغطية لهذه المهمّة.',
    coverageAssumption: 'المُغطّى مرسوم على افتراض نصف قطر الرؤية أعلاه، ولا نسبة مئوية معه عمداً — رقمٌ واثق يوقف البحث قبل أوانه.',
    metres: 'م',

    actions: 'الإجراء',
    publish: 'تصريح وبدء',

    // §23.2's committee path. Distinct from «تصريح وبدء», which is the Head of Control deciding
    // without a vote — the answer form allows both, and the two must never read as one button.
    submitToCommittee: 'إرسال إلى اللجنة',
    startMission: 'بدء المهمّة',
    close: 'إقفال',
    startRound: 'جولة جديدة',
    endRound: 'إنهاء الجولة',
    issueCertificates: 'أصدر شهادات الشكر',
    certificatesIssued: 'صدرت {n} شهادة.',
    certificatesAlreadyIssued: 'كلّ من له شهادة يملكها بالفعل.',
    noActions: '—',
    actionFailed: 'تعذّر تنفيذ الإجراء.',

    closeDialogTitle: 'إقفال المهمّة',
    closeDialogLede: 'النتيجة تدخل التقرير النهائي والإحصاءات، فاخترها كما جرت.',
    outcomeLabel: 'النتيجة',
    outcomePlaceholder: 'اختر النتيجة',
    outcomeRequired: 'النتيجة مطلوبة.',
    summaryLabel: 'ملخّص (اختياري)',
    summaryPlaceholder: 'ما جرى باختصار',
    confirmClose: 'تأكيد الإقفال',
    cancel: 'إلغاء',
    closeAria: 'إغلاق',

    roundTitleLabel: 'اسم الجولة',
    roundTitlePlaceholder: 'مثال: الجولة الثانية — الشعيب الشرقي',
    roundTitleRequired: 'اسم الجولة مطلوب.',
    startRoundDialogTitle: 'بدء جولة جديدة',
    startRoundDialogLede: 'الجولة تُعيد تقسيم القطاعات على الأعضاء الحاضرين.',
    confirmStartRound: 'بدء الجولة',

    // لواحق المدّة، مفصولة عن الأرقام لأن `formatDuration` مُنسِّق مشترك و§5.8 تمنع
    // النصوص داخل الشيفرة.
    unitDay: 'ي',
    unitHour: 'س',
    unitMinute: 'د',



    detailLeader: 'قائد المهمّة',
    detailLeaderColumn: 'تحكّم المهمّة',
    detailNameLeader: 'ترشيح قائداً',
    detailStandDownLeader: 'إلغاء الترشيح',
    detailLeaderHint: 'قائد واحد للمهمّة — ترشيح آخر يُلغي الأوّل.',
    detailRemoveMember: 'إخراج',
    detailRemoveDialogTitle: 'إخراج من المهمّة',
    detailRemoveDialogLede: 'يفقد العضو الخريطة والمحادثة فوراً، ويصله سبب الإخراج.',
    detailRemoveReason: 'السبب',
    detailRemoveReasonPlaceholder: 'مثال: غادر نطاق البحث دون إذن.',
    detailRemoveReasonRequired: 'سبب الإخراج مطلوب.',
    detailConfirmRemove: 'تأكيد الإخراج',
    detailControlOnly: 'لتحكّم المهمّة',

    chatControlCanAnnounce: 'ما يقوله الفريق في الميدان — ولك أن تُرسل إشعاراً يصل أجهزتهم.',
    announceHeading: 'إشعار من التحكّم',
    announceLede: 'يظهر في المحادثة إشعاراً، ويصل أجهزة كلّ من على المهمّة.',
    announcePlaceholder: 'مثال: عودوا إلى نقطة التجمّع، الطقس يتغيّر.',
    announceSend: 'إرسال الإشعار',

    // ── التحكّم في المهمّة (§23.1) ───────────────────────────────────────────────
    controlHeading: 'التحكّم',
    controlHolder: 'المتولّي',
    controlSince: 'منذ',
    controlUnattended: 'لا يتولّى هذه المهمّة أحد.',
    controlOfferQuestion: 'هل تريد أن تكون التحكّم لهذه المهمّة؟',
    controlAccept: 'نعم، أتولّاها',
    controlDecline: 'لا',
    controlYouAccepted: 'قبلتَ تحكّم هذه المهمّة — بانتظار إسناد رئيس التحكّم.',
    controlYouDeclined: 'اعتذرتَ عن تحكّم هذه المهمّة.',
    controlYouHold: 'أنت المتولّي.',
    controlChangeAnswer: 'تغيير الجواب',
    controlCandidates: 'الترشّحات',
    controlNoCandidates: 'لم يُجب أحد بعد.',
    controlAccepted: 'قَبِل',
    controlDeclined: 'اعتذر',
    controlAssign: 'إسناد',
    controlAssignHint: 'يُسنَد لواحد ممّن قبلوا. للمهمّة تحكّم واحد، والانتقال بالتسليم.',
    controlHandover: 'تسليم الوردية',
    controlHandoverTo: 'التسليم إلى',
    controlHandoverNobody: 'إنهاء بلا خلف',
    controlHandoverNotes: 'ما يحتاج التالي معرفته',
    controlHandoverNotesPlaceholder: 'مثال: القطاع الشمالي غُطّي، والفريق الثاني في الشعيب.',
    controlHandoverConfirm: 'تسليم',
    controlHandoverWarning: 'إنهاء الوردية بلا خلف يترك المهمّة بلا متولٍّ.',
    controlHistory: 'الورديات',
    controlShiftEnded: 'انتهت',
    controlShiftOpen: 'مفتوحة',
    controlHandedTo: 'سُلِّمت إلى',
    controlEndedAlone: 'انتهت بلا خلف',

    // ── تفاصيل المهمّة ─────────────────────────────────────────────────────────────
    detailBack: 'رجوع إلى اللوحة',
    detailNotFound: 'لا توجد مهمّة بهذا المعرّف.',
    detailOverview: 'الحالة',
    detailTimes: 'الأوقات',
    detailIncidentAt: 'وقت الحادثة',
    detailApprovedAt: 'الاعتماد',
    detailStartedAt: 'البدء',
    detailClosedAt: 'الإقفال',
    detailOutcome: 'النتيجة',
    detailVisibilityRadius: 'نصف قطر الرؤية',
    detailIdleThreshold: 'عتبة السكون',
    detailNotificationScope: 'نطاق الإشعار',
    detailCapacity: 'السعة',
    detailNone: '—',

    detailCommittee: 'التصويت',
    detailApprovals: 'موافقة',
    detailRejections: 'رفض',
    detailAbstentions: 'امتناع',
    detailQuorum: 'النصاب المطلوب',
    detailDeadline: 'المهلة',
    detailAwaitingEscalation: 'انتهت المهلة — القرار لرئيس الكنترول',

    detailRounds: 'الجولات',
    detailNoRounds: 'لا جولات في هذه المهمّة.',
    detailRoundSequence: 'الجولة',
    detailRoundTitle: 'الاسم',
    detailRoundStarted: 'بدأت',
    detailRoundEnded: 'انتهت',
    detailRoundOpen: 'مفتوحة',
    detailRoundArea: 'المساحة',

    // §23.6: المشاركة يعتمدها رئيس الكنترول يدوياً، وما تدين له اللوحة به هو الدليل —
    // المدّة والمسافة والتقرير. ولذلك الأعمدة هذه بالذات.
    detailMembers: 'الأعضاء',
    detailNoMembers: 'لم ينضمّ أحد بعد.',
    detailMemberName: 'العضو',
    detailMemberState: 'الحالة',
    detailMemberSector: 'القطاع',
    detailMemberJoined: 'انضمّ',
    detailMemberDuration: 'المدّة',
    detailMemberDistance: 'المسافة',
    detailMemberCounted: 'المشاركة',
    detailCreditParticipation: 'اعتماد المشاركة',
    detailWithdrawParticipation: 'سحب الاعتماد',
    detailCreditPending: 'تُعتمد المشاركة بعد انتهاء المهمة.',
    detailCreditedBy: 'اعتمدها',
    detailKnight: 'فارس',
    detailNameKnight: 'تسمية فارساً',
    detailWithdrawKnight: 'سحب التسمية',
    detailKnightUnavailable: 'تُمنح لمهامّ المفقودين بعد العثور عليه.',
    detailPoints: 'النقاط',
    detailCounted: 'مُعتمدة',
    detailNotCounted: 'غير مُعتمدة',
    detailIdleWarned: 'أُنذر بالسكون',
    detailReinstate: 'إعادة إدراج',
    detailReinstateHint: 'أُزيل للسكون — أعِده إن كان يبحث فعلاً',
    detailRemoved: 'أُزيل',
    kilometres: 'كم',

    detailReport: 'تقرير الإقفال',
    detailNoReport: 'لا تقرير بعد. يُكتب بعد الإقفال.',
    detailReportPreparedBy: 'أعدّه',
    detailReportApprovedBy: 'اعتمده',
    detailReportPending: 'بانتظار الاعتماد',
    detailReportApproved: 'مُعتمد',
    detailWriteReport: 'كتابة التقرير',
    detailReviseReport: 'تعديل التقرير',
    detailApproveReport: 'اعتماد التقرير',
    detailReportDialogTitle: 'تقرير المهمّة',
    detailReportDialogLede: 'النتيجة نتيجة المهمّة، ثُبِّتت عند الإقفال — التقرير يصف ما جرى.',
    detailReportSummaryLabel: 'نصّ التقرير',
    detailReportSummaryRequired: 'نصّ التقرير مطلوب.',
    detailReportSave: 'حفظ',
    detailReportApprovedLocked: 'التقرير مُعتمد ولا يقبل التعديل.',
    detailRoundReport: 'تقرير الجولة',
    detailRoundWriteReport: 'اكتب تقرير الجولة',
    detailRoundReviseReport: 'راجع تقرير الجولة',
    detailRoundNoReport: 'لم يُكتب بعد',
    detailRoundReportDialogTitle: 'تقرير الجولة',
    detailRoundReportDialogLede: 'ما غطّته هذه الجولة وما وُجد فيها. يُقتبَس في تقرير المهمّة المعتمد.',
    detailReportFile: 'فتح التقرير (PDF)',
    detailReportFileOpening: 'يُفتح…',
    detailReportFileUnavailable: 'تعذّر فتح ملفّ التقرير.',

    detailTimeline: 'سجلّ المهمّة',
    detailNoTimeline: 'لا أحداث بعد.',
    detailTimelineActor: 'بواسطة',
    detailTimelineSystem: 'النظام',
  },

  missionStatus: {
    Draft: 'مسوّدة',
    PendingCommittee: 'عند اللجنة',
    Approved: 'معتمدة',

    // اللجنة لم تُصدر تصريحاً — حالة يجب أن تظهر في اللوحة لا أن تسقط منها بصمت.
    Rejected: 'رفضتها اللجنة',
    Active: 'جارية',
    Closed: 'مغلقة',
    Cancelled: 'ملغاة',
  },

  // ⚠️ §23.3's own table, word for word — these are not our phrasing to shorten.
  //
  // The seven are the client's answer to answer 8, and the outcome is written onto the mission, the
  // final report and the statistics permanently. The panel used to carry a tidier abbreviation
  // ('عُثر عليه سالماً'), which drops the word المفقود and reads identically on a stranded mission —
  // a closure summary that no longer says who was found. A report that quotes the panel must quote
  // the requirement, so the wording is copied from §23.3 rather than edited for length.
  statistics: {
    heading: 'إحصاءات الجمعية',
    lede: 'ما جرى في المدّة المختارة، وحال السجلّ الآن.',
    from: 'من',
    to: 'إلى',
    loading: 'تُحسب الأرقام…',

    periodHeading: 'خلال المدّة',
    periodLede: 'يتغيّر بتغيّر التاريخين. المدّة الافتراضية آخر اثني عشر شهراً.',

    rosterHeading: 'الآن',
    rosterLede: 'لا يتأثّر بالتاريخين: هذه حال السجلّ اليوم، لا في مدّة مضت.',

    missionsOpened: 'مهامّ فُتحت',
    participationsCredited: 'مشاركات معتمدة',
    hoursOnTheGround: 'ساعات في الميدان',
    distanceCovered: 'المسافة المقطوعة (كم)',
    knightsAwarded: 'وسام الفارس',
    certificatesIssued: 'شهادات صادرة',

    missionsRunning: 'مهامّ جارية',
    volunteersApproved: 'متطوّعون معتمدون',
    volunteersAwaitingReview: 'بانتظار المراجعة',

    byMonth: 'المهامّ شهراً بشهر',
    byMonthLede: 'الشهر الخالي صفر لا فراغ — الموسم الهادئ جزء من الصورة.',
    byOutcome: 'حسب النتيجة',
    byType: 'حسب النوع',

    noOutcomes: 'لم تُقفل مهمّة في هذه المدّة.',
    noMissions: 'لم تُفتح مهمّة في هذه المدّة.',
    noVolunteers: 'لا متطوّعين معتمدين بعد.',
  },

  missionOutcome: {
    FoundAlive: 'عُثر على المفقود سالماً',
    FoundDeceased: 'عُثر على المفقود متوفّى',
    StrandedRescued: 'تمّ إنقاذ العالق',
    SearchCancelled: 'أُلغي البحث',
    FoundByAnotherParty: 'عثر على المفقود من جهة أخرى',
    HandedToAuthorities: 'سُلّمت المهمة لجهة أخرى',
    ReturnedByThemselves: 'عاد المفقود من تلقاء نفسه',
  },


  // ⚠️ حالة العضو كانت تُطبع **باسم التعداد الإنجليزيّ خامًا** في جدول يقرأه الكنترول أثناء بحث —
  // «Joined»، «Apologised» — وهو العطل نفسه الذي أُصلح ثلاث مرّات قبله في حالة المهمّة ونطاق
  // الإشعار وسجلّ الأحداث. قِيس في اللوحة على مهمّة فرعيّة في ٧ سبتمبر ٢٠٢٦.
  memberState: {
    Invited: 'مدعوّ',
    Joined: 'منضمّ',
    Active: 'في الميدان',
    IdleWarned: 'تنبيه سكون',
    IdleRemoved: 'أُخرج للسكون',
    Left: 'غادر',
    Completed: 'أنهى مشاركته',
    Apologised: 'معتذر',
    Excluded: 'مُستبعَد',
    Waiting: 'في قائمة الانتظار',
  },

  missionType: {
    Missing: 'مفقود',
    Stranded: 'عالق',
    SubMission: 'مهمّة فرعية',
  },

  // ⚠️ Raw enum names were rendered here — «AllUsers» in an Arabic sentence about who gets called
  // out. The scope is a decision the Head of Control makes and has to be able to read back.
  notificationScope: {
    AllUsers: 'كل المستخدمين',
    SameRegion: 'متطوّعو المنطقة نفسها',
  },

  // ⚠️ The mission timeline printed `Created`, `SectorsDivided`, `MemberJoined` — the enum's own
  // spelling — in the panel the control room reads during a search.
  missionEvent: {
    Created: 'أُنشئت',
    SubmittedToCommittee: 'أُحيلت إلى اللجنة',
    Approved: 'اعتُمدت',
    Rejected: 'رفضتها اللجنة',
    Started: 'بدأت',
    MemberJoined: 'انضمّ عضو',
    MemberRemoved: 'أُخرج عضو',
    MemberReinstated: 'أُعيد إدراج عضو',
    FirstSweepDone: 'اكتمل المسح الأول',
    PersonFound: 'عُثر على الشخص',
    Closed: 'أُقفلت',
    SectorsDivided: 'قُسِّمت القطاعات',
    LeaderNamed: 'سُمّي قائد الميدان',
    CommitteeEscalated: 'انتهت مهلة اللجنة',
    FoundReported: 'أبلغ منقذ بالعثور',
  },

  committee: {
    heading: 'بلاغات بانتظار تصويت اللجنة',
    lede: 'التصويت قرار على بلاغ مفقود — والمهلة تمرّ.',
    empty: 'لا توجد بلاغات بانتظار التصويت.',
    person: 'المفقود',
    age: 'العمر',
    missedAt: 'آخر ظهور',
    lastSeen: 'مكان آخر ظهور',
    clothing: 'الملابس',
    votes: 'التصويت',
    deadline: 'المهلة',
    deadlinePassed: 'انتهت المهلة',
    approve: 'موافقة',
    reject: 'رفض',
    voted: 'صوّتَّ',
    restrictedNotes: 'توجد ملاحظات مقيَّدة',
    voteFailed: 'تعذّر تسجيل التصويت.',
    quorumReached: 'بلغ النصاب',
    ofRequired: 'من',
    years: 'سنة',
  },

  signIn: {
    heading: 'تسجيل الدخول',
    lede: 'يُرسَل رمز تحقق إلى جوالك.',
    phoneLabel: 'رقم الجوال',
    phonePlaceholder: '05XXXXXXXX',
    requestCode: 'إرسال الرمز',
    codeLabel: 'رمز التحقق',
    codePlaceholder: '______',
    verify: 'تأكيد',
    changeNumber: 'تغيير الرقم',
    codeSent: 'إن كان الرقم مسجَّلاً فقد أُرسل رمز.',
    invalidPhone: 'رقم الجوال غير صحيح.',
    invalidCode: 'الرمز يجب أن يكون ٦ أرقام.',
    rejected: 'الرمز غير صحيح أو انتهت صلاحيته.',
    tooMany: 'محاولات كثيرة. انتظر قليلاً ثم أعد المحاولة.',
    failed: 'تعذّر تسجيل الدخول. حاول مرة أخرى.',
    notForYou: 'لوحة التحكّم لإدارة الجمعيّة. عملُك على تطبيق عون.',
  },

  stats: {
    increase: 'ارتفاع',
    decrease: 'انخفاض',
  },

  pagination: {
    label: 'التنقّل بين الصفحات',
    previous: 'السابق',
    next: 'التالي',
    of: 'من',
  },

  users: {
    heading: 'الصلاحيات',
    lede: 'من يستطيع فعل ماذا. الدور هو ما يفتح شاشة أو يغلقها.',
    empty: 'لا يوجد أحد في هذه القائمة.',
    search: 'ابحث بالاسم، أو برقم الجوال كاملاً',
    // ⚠️ Said on the screen, not hidden in a code comment: the number is stored behind a value
    // object with a converter, so only a whole number matches. An administrator who types the last
    // six digits and finds nobody would otherwise conclude the person is not registered.
    searchHint: 'البحث بالجوال يطابق الرقم كاملاً، لا جزءاً منه.',
    searchPlaceholder: 'اسم، أو رقم جوال كامل',
    confirmRemoveTitle: 'سحب دور',
    confirmRemoveLede: 'الدور هو ما يفتح شاشة أو يغلقها. سحبه يسري فوراً.',
    filters: 'التصفية',
    clearFilters: 'مسح',
    roleFilter: 'الدور',
    listTitle: 'المستخدمون',
    emptyFiltered: 'لا أحد يطابق هذه التصفية.',
    emptyAll: 'لا يوجد مستخدمون بعد.',
    name: 'الاسم',
    phone: 'الجوال',
    region: 'المنطقة',
    roles: 'الأدوار',
    account: 'الحساب',
    suspend: 'إيقاف',
    suspended: 'موقوف — أعِده',
    noRoles: 'بلا دور',
    add: 'إضافة دور',
    remove: 'سحب',
    filterAll: 'الكل',
    changeFailed: 'تعذّر تغيير الدور.',
    // ⚠️ Names the person and the role, because a confirmation that says "are you sure?" is a
    // confirmation nobody reads. Withdrawing control from the wrong colleague mid-search is the
    // mistake this sentence exists to stop.
    confirmRemove: 'سحب دور {role} من {name}؟',
    confirmRemoveYes: 'سحب الدور',
    cancel: 'إلغاء',
    roleNames: {
      'reporter': 'مبلّغ',
      'organisation-representative': 'ممثل جهة',
      'field-volunteer': 'متطوّع ميداني',
      'committee-member': 'عضو لجنة المشاركات',
      'head-of-control': 'رئيس الكنترول',
      'desk-control': 'كنترول مكتبي',
      'field-leader': 'قائد الميدان',
      'association-employee': 'موظّف الجمعية',
      'system-administrator': 'مدير النظام',
    },
  },

  volunteers: {
    heading: 'المتطوّعون',
    queueHeading: 'بانتظار مراجعة الوثائق',
    queueLede: 'الاعتماد هو ما يمنح المتطوّع صلاحية الوصول إلى بيانات المهامّ.',
    empty: 'لا يوجد متطوّعون في هذه القائمة.',
    name: 'الاسم',
    region: 'المنطقة',
    type: 'النوع',
    equipment: 'العدّة',
    documents: 'الوثائق',
    status: 'الحالة',
    registered: 'تاريخ التسجيل',
    actions: 'الإجراء',
    approve: 'اعتماد',
    reject: 'رفض',
    documentAttached: 'مرفقة',
    documentMissing: 'لا توجد',
    openDocument: 'فتح الوثيقة',
    openCertificate: 'فتح الشهادة',
    openingDocument: 'جارٍ فتح الوثيقة…',
    documentUnavailable: 'تعذّر فتح الوثيقة.',
    documentAuditNotice: 'كل فتح للوثيقة يُسجَّل باسمك.',
    has4wd: 'دفع رباعي',
    hasRadio: 'لاسلكي',
    hasGps: 'جهاز تتبّع',
    noEquipment: '—',
    rejectReasonPrompt: 'سبب الرفض',
    rejectDialogTitle: 'رفض المتطوّع',
    rejectDialogLede: 'السبب يُبلَّغ للمتطوّع، فاكتبه كما تريد أن يقرأه.',
    rejectReasonLabel: 'السبب',
    rejectReasonPlaceholder: 'مثال: صورة الهوية غير مقروءة',
    rejectReasonRequired: 'السبب مطلوب.',
    previousRejection: 'رُفض سابقاً:',
    accountSuspended: 'الحساب موقوف — لا يستطيع الدخول',
    accountNeverSignedIn: 'لم يدخل بعد — يُفعَّل حسابه عند أوّل تسجيل دخول',
    approveNeedsDocument: 'لا يمكن الاعتماد قبل رفع وثيقة الهوية.',
    confirmReject: 'تأكيد الرفض',
    cancel: 'إلغاء',
    close: 'إغلاق',
    reviewFailed: 'تعذّر تنفيذ المراجعة.',
    filterAll: 'الكل',
  },

  approvalStatus: {
    Pending: 'بانتظار المراجعة',
    Approved: 'معتمد',
    Rejected: 'مرفوض',
  },

  volunteerType: {
    MissingField: 'ميداني — مفقودين',
    StrandedField: 'ميداني — عالقين',
    Initiative: 'مبادرات',
  },

  attention: {
    title: 'يحتاج انتباهك',
    open: 'التنبيهات',
    close: 'إغلاق',
    nothing: 'لا شيء بانتظارك الآن.',
    failed: 'تعذّر جلب التنبيهات.',
    foundTitle: 'بلاغ عثور من الميدان',
    foundBy: 'أبلغ',
    volunteers: 'متطوعون بانتظار مراجعة الوثائق',
    missionWaiting: 'مهمّة بانتظار قرار',
    condition: {
      Safe: 'سالم',
      NeedsMedicalHelp: 'يحتاج إسعافًا',
      Deceased: 'متوفّى',
      unknown: 'الحالة غير محدّدة',
    },
  },

  errors: {
    unauthorized: 'انتهت الجلسة. سجّل الدخول من جديد.',
    forbidden: 'لا تملك صلاحية هذا الإجراء.',
    generic: 'حدث خطأ غير متوقّع.',
    retry: 'إعادة المحاولة',
  },
} as const;

/**
 * Widens the literal types `as const` produces, so `en.ts` can hold *different* strings
 * while still being required to hold the *same keys*.
 *
 * Without the widening, `Dictionary` demands the literal `"عون"` and the English file
 * cannot satisfy it — which is what the first version of this file did. The point of
 * deriving the type from Arabic is key parity (§23.10: a string in one locale and not the
 * other is a defect), not value equality.
 */
type Widen<T> = T extends string ? string : { [K in keyof T]: Widen<T[K]> };

export type Dictionary = Widen<typeof ar>;
